/* eslint-disable max-lines -- Pre-existing violation */
import {
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  type FormSubmitHandler,
  Separator,
  Tabs,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useArchiveModelYearMutation,
  useGetAuthorConfiguratorBundleQuery,
  useGetMakeQuery,
  useGetModelQuery,
  useGetModelYearQuery,
  useGetModelYearReadinessQuery,
  useGetVariantMatrixQuery,
  useListColourQuery,
  useListEngineQuery,
  useListFeatureQuery,
  useListFeatureRuleQuery,
  useListPackageQuery,
  useListTrimQuery,
  useListUpholsteryQuery,
  useUpdateModelYearMutation,
  type ModelYearResponseBody,
  type VatRateType,
} from '@dms/api/new-vehicle';
import {
  getNewVehicleEnumColorScheme,
  getNewVehicleEnumLabel,
  NewVehiclePageHeader,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {SkeletonLoadingBox, useGetVatRatesOptions, usePermissions, useTenant} from '@dms/shared';

import {composePath, parseDate, useNavigate, type ApiError} from 'shared/web';

import {Engines, TrimLevels} from './CatalogueTables';
import {
  dateWindowRefinement,
  failingChecks,
  FormSection,
  handleCatalogueError,
  isoDate,
  optionalIsoDate,
  t,
} from './catalogueUi';
import {ColourUpholsteryCombinations, Colours, Upholsteries} from './ColoursUpholstery';
import {DuplicateModelYearDialog} from './DuplicateModelYearDialog';
import {EquipmentByTrimTab, FeaturesTab, PackagesTab, RulesTab} from './EquipmentAuthoring';
import {ImporterPriceListFields} from './ImporterPriceListFields';
import {IMPORTER_PRICE_LIST_FIELD_NAMES, importerPriceListSchema} from './importerPriceListSchema';
import {pricelistParameters} from './pricelistParameters';
import {
  PublishBlockedDialog,
  PublishChecklist,
  PublishWarningsDialog,
  type ReadinessFailure,
} from './PublishChecklist';
import {sectionIcons} from './sectionIcons';
import {tabLabels, type TabId} from './tabLabels';
import {useModelYearLifecycle} from './useModelYearLifecycle';
import {VariantMatrix} from './VariantMatrix';

const MODEL_YEAR_PREVIEW_ROUTE =
  '/vehicles/new-vehicle-catalogue/model-year/:modelYearId/preview/step/:step';
const VAT_RATE_TYPES = [
  'S',
  'R',
  'MR',
  'E',
  'Z',
  'SR',
  'P',
] as const satisfies readonly VatRateType[];
const overviewSchema = z
  .object({
    code: z.string().min(1),
    catalogueName: z.string().trim().min(1),
    pricelistVersionLabel: z.string().trim().min(1),
    vatRateType: z.enum(VAT_RATE_TYPES),
    validFrom: isoDate,
    validTo: optionalIsoDate,
    importerPriceList: importerPriceListSchema,
    version: z.number(),
  })
  .superRefine(dateWindowRefinement('validFrom', 'validTo'));
type OverviewValues = z.infer<typeof overviewSchema>;
const tabs = Object.keys(tabLabels) as (keyof typeof tabLabels)[];
// undefined while the count loads, null when a query it needs failed before it ever loaded.
type ModelYearCounts = Partial<Record<TabId, number | null>>;
type OverviewProps = {
  modelYear: ModelYearResponseBody;
  currency: string;
  canWrite: boolean;
  counts: ModelYearCounts;
  onSave: (values: OverviewValues) => Promise<ModelYearResponseBody | undefined>;
  onTab: (tab: TabId) => void;
};

const isVatRateType = (value: string): value is VatRateType =>
  VAT_RATE_TYPES.includes(value as VatRateType);
const isTabId = (value: string): value is TabId => tabs.some((tab) => tab === value);
const errorDetails = (error: unknown): ReadinessFailure[] => {
  if (typeof error !== 'object' || error === null) {
    return [];
  }
  const payload = 'data' in error ? error.data : 'errors' in error ? error.errors : error; // eslint-disable-line no-nested-ternary -- Pre-existing violation
  if (typeof payload !== 'object' || payload === null) {
    return [];
  }
  const readiness = 'readiness' in payload ? payload.readiness : payload;
  if (
    typeof readiness !== 'object' ||
    readiness === null ||
    !('errors' in readiness) ||
    !Array.isArray(readiness.errors)
  ) {
    return [];
  }
  return readiness.errors.flatMap((item) => {
    if (
      typeof item !== 'object' ||
      item === null ||
      !('code' in item) ||
      typeof item.code !== 'string' ||
      !('message' in item) ||
      typeof item.message !== 'string' ||
      !('tab' in item) ||
      typeof item.tab !== 'string' ||
      !isTabId(item.tab)
    ) {
      return [];
    }
    return [{code: item.code, message: item.message, tab: item.tab}];
  });
};

type CountSource = {isError: boolean; isFetching: boolean; startedTimeStamp?: number};

// A request started before this model year was opened (Duplicate invalidates, then navigates) can
// return data that predates it: its count waits, and is unknown if that request fails. Requests
// started after opening, such as the refetch after a save, keep showing the current count. RTK drops
// invalidated lists nobody subscribes to, so arriving from another page always loads them afresh.
const countOrPending = (openedAt: number, sources: CountSource[], count: number | undefined) => {
  const predatesOpening = (source: CountSource) => (source.startedTimeStamp ?? 0) < openedAt;
  // A never-loaded count is unknown on any failure, whenever its request started.
  return match(count)
    .with(undefined, () => (sources.some((source) => source.isError) ? null : undefined))
    .when(
      () => sources.some((source) => source.isError && predatesOpening(source)),
      () => null
    )
    .when(
      () => sources.some((source) => source.isFetching && predatesOpening(source)),
      () => undefined
    )
    .otherwise((loaded) => loaded);
};

function useModelYearCounts(modelYearId: string) {
  const trims = useListTrimQuery({});
  const engines = useListEngineQuery({});
  const matrix = useGetVariantMatrixQuery({modelYearId});
  const colours = useListColourQuery({});
  const upholsteries = useListUpholsteryQuery({});
  const features = useListFeatureQuery({});
  const bundle = useGetAuthorConfiguratorBundleQuery({modelYearId});
  const packages = useListPackageQuery({});
  const rules = useListFeatureRuleQuery({});
  const [opened, setOpened] = useState({modelYearId, at: Date.now()});
  if (opened.modelYearId !== modelYearId) {
    setOpened({modelYearId, at: Date.now()});
  }
  // currentData, not data: data still holds the previous model year's result after navigating.
  const featureIds = new Set(bundle.currentData?.features.map((feature) => feature.id) ?? []);

  const counts: ModelYearCounts = {
    'trim-levels': countOrPending(
      opened.at,
      [trims],
      trims.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived).length
    ),
    engines: countOrPending(
      opened.at,
      [engines],
      engines.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived)
        .length
    ),
    'variant-matrix': countOrPending(
      opened.at,
      [matrix],
      matrix.currentData?.cells.filter((item) => !item.archived).length
    ),
    colours: countOrPending(
      opened.at,
      [colours],
      colours.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived)
        .length
    ),
    upholstery: countOrPending(
      opened.at,
      [upholsteries],
      upholsteries.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived)
        .length
    ),
    // The library lists every model year's features; until the bundle scopes it, any count is wrong.
    features: countOrPending(
      opened.at,
      [features, bundle],
      bundle.currentData
        ? features.data?.items.filter((item) => featureIds.has(item.id) && !item.archived).length
        : undefined
    ),
    packages: countOrPending(
      opened.at,
      [packages],
      packages.data?.items.filter(
        (item) => item.modelYearId === modelYearId && !item.archived && item.status !== 'archived'
      ).length
    ),
    rules: countOrPending(
      opened.at,
      [rules],
      rules.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived).length
    ),
  };
  const publishableVariants = matrix.currentData?.cells.filter(
    (item) => item.enabled && !item.archived
  ).length;

  return {counts, publishableVariants, isMatrixFetching: matrix.isFetching};
}

/** Catalogue parts summarised on the overview, in the order a model year is usually authored. */
const STAT_TABS = [
  'trim-levels',
  'engines',
  'variant-matrix',
  'colours',
  'upholstery',
  'features',
] as const satisfies readonly TabId[];

const StatIcon = styled.span`
  display: inline-flex;
`;

const StatChevron = styled.span`
  display: inline-flex;
  opacity: 0;
  translate: -${({theme}) => theme.getSize(1)} 0;
  transition-property: opacity, translate;
  transition-duration: 150ms;
  transition-timing-function: ease-out;
`;

const StatTileButton = styled.button<{$isEmpty: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({theme}) => theme.getSize(1)};
  width: 100%;
  padding: ${({theme}) => theme.getSize(4)};
  border: 1px solid transparent;
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme}) => theme.colors.general.white};
  box-shadow: ${({theme}) => theme.shadows.elevation_1};
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition-property: border-color, box-shadow;
  transition-duration: 150ms;
  transition-timing-function: ease-out;
  ${({theme, $isEmpty}) =>
    $isEmpty ? `border-color: ${theme.colors.palettes.orange[30][100]};` : ''}

  &:hover ${StatChevron}, &:focus-visible ${StatChevron} {
    opacity: 1;
    translate: 0 0;
  }

  &:hover {
    border-color: ${({theme, $isEmpty}) =>
      $isEmpty ? theme.colors.palettes.orange[60][100] : theme.colors.palettes.neutral[40][100]};
    box-shadow: ${({theme}) => theme.shadows.elevation_2};
  }

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.general.accent};
    outline-offset: 2px;
  }
`;

/** A count per catalogue part; clicking one opens its tab, an empty one is flagged. */
function CatalogueStats(props: {counts: ModelYearCounts; onTab: (tab: TabId) => void}) {
  return (
    <Grid columns={[2, 3, 6]} spacing={3}>
      {STAT_TABS.map((tab) => {
        const count = props.counts[tab];
        const isEmpty = count === 0;
        const isLoading = count === undefined;
        return (
          <StatTileButton
            key={tab}
            type="button"
            $isEmpty={isEmpty}
            onClick={() => props.onTab(tab)}
            aria-busy={isLoading}
            aria-label={`${tabLabels[tab]}: ${count ?? '\u2014'}`}
          >
            <HStack spacing={1} align="center" justify="space-between" width="100%">
              <HStack spacing={2} align="center">
                <StatIcon aria-hidden="true">
                  <Icon value={sectionIcons[tab]} size={4} color="text.secondary" />
                </StatIcon>
                <Text size="xSmall" color="secondary" alternative>
                  {tabLabels[tab]}
                </Text>
              </HStack>
              <StatChevron>
                <Icon value="navigation/chevron_right" size={4} color="text.secondary" />
              </StatChevron>
            </HStack>
            {/* Not SkeletonLoadingBox's own toggle: its exit fade would keep the stale number on screen. */}
            <Show when={isLoading}>
              <SkeletonLoadingBox isLoading width={10} height={8} />
            </Show>
            <Show when={!isLoading}>
              <Heading size={2}>{isNil(count) ? '\u2014' : count.toString()}</Heading>
            </Show>
            <Show when={isEmpty}>
              <Text size="xSmall" color="warning">
                {t('entity.newVehicle.catalogue.statEmpty', 'None yet')}
              </Text>
            </Show>
          </StatTileButton>
        );
      })}
    </Grid>
  );
}

function Overview(props: OverviewProps) {
  return (
    <VStack spacing={4}>
      <CatalogueStats counts={props.counts} onTab={props.onTab} />
      <Grid columns={[1, 1, 3]} spacing={4} align="flex-start">
        <ModelYearDetails {...props} />
        <GridItem span={[1, 1, 2]}>
          <Show when={props.modelYear.status === 'draft'}>
            <PublishChecklist modelYearId={props.modelYear.id} onTab={props.onTab} />
          </Show>
        </GridItem>
      </Grid>
    </VStack>
  );
}

function ModelYearDetails(props: OverviewProps) {
  const [tenantVatOptions] = useGetVatRatesOptions({fullLabel: true});
  // The backend accepts only its VAT type enum; the saved type stays listed even without a tenant rate.
  const savedType = props.modelYear.vatRateType;
  const vatOptions = [
    ...tenantVatOptions.filter(({value}) => isVatRateType(value)),
    ...(isVatRateType(savedType) && !tenantVatOptions.some(({value}) => value === savedType)
      ? [{value: savedType, label: savedType}]
      : []),
  ];
  const isLocked = props.modelYear.status === 'active';
  const lockedHelper = isLocked
    ? t(
        'entity.newVehicle.catalogue.lockedWhenPublished',
        'Locked while the model year is published.'
      )
    : undefined;
  const submit: FormSubmitHandler<OverviewValues> = async (values, _setErrors, reset) => {
    const saved = await props.onSave(values);
    if (!saved) {
      return;
    }
    reset({
      code: saved.name,
      catalogueName: saved.catalogueName,
      pricelistVersionLabel: saved.pricelistVersionLabel,
      vatRateType: isVatRateType(saved.vatRateType) ? saved.vatRateType : 'S',
      validFrom: saved.validFrom,
      validTo: saved.validTo ?? null,
      importerPriceList: saved.importerPriceList,
      version: saved.version,
    });
  };
  return (
    <Card title={t('entity.newVehicle.catalogue.modelYearDetails', 'Model year details')}>
      <Form<OverviewValues>
        key={props.modelYear.id}
        experimentalZodSchema={overviewSchema}
        resetOptions={{keepDirtyValues: !isLocked}}
        values={{
          code: props.modelYear.name,
          catalogueName: props.modelYear.catalogueName,
          pricelistVersionLabel: props.modelYear.pricelistVersionLabel,
          vatRateType: isVatRateType(props.modelYear.vatRateType)
            ? props.modelYear.vatRateType
            : 'S',
          validFrom: props.modelYear.validFrom,
          validTo: props.modelYear.validTo ?? null,
          importerPriceList: props.modelYear.importerPriceList,
          version: props.modelYear.version,
        }}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="code"
              type="text"
              label={t('entity.newVehicle.catalogue.code', 'Code')}
              helperText={
                lockedHelper ??
                t(
                  'entity.newVehicle.catalogue.codeHelp',
                  'Shown to salespeople next to the model name, for example MY2025.'
                )
              }
              isDisabled={!props.canWrite || isLocked}
              isRequired
            />
            <FormField
              control={control}
              name="catalogueName"
              type="text"
              label={t('entity.newVehicle.catalogue.catalogueName', 'Catalogue name')}
              placeholder={t(
                'entity.newVehicle.authoring.catalogueNamePlaceholder',
                'e.g. Puma retail 2027'
              )}
              isDisabled={!props.canWrite}
              isRequired
            />
            <FormField
              control={control}
              name="pricelistVersionLabel"
              type="text"
              label={t('entity.newVehicle.catalogue.pricelistVersionLabel', 'Price list version')}
              placeholder={t(
                'entity.newVehicle.authoring.priceListVersionPlaceholder',
                'e.g. 2027/1'
              )}
              isDisabled={!props.canWrite}
              isRequired
            />
            <FormField
              control={control}
              name="vatRateType"
              type="choice"
              menuInPortal
              isNotClearable
              label={t('general.labels.vatRate', 'VAT rate')}
              options={vatOptions}
              helperText={lockedHelper}
              isDisabled={!props.canWrite || isLocked}
              isRequired
            />
            <Grid columns={[1, 2, 1, 2]} spacing={3}>
              <FormField
                control={control}
                name="validFrom"
                type="apiDate"
                label={t('entity.newVehicle.catalogue.validFrom', 'Valid from')}
                helperText={lockedHelper}
                isDisabled={!props.canWrite || isLocked}
              />
              <FormField
                control={control}
                name="validTo"
                type="apiDate"
                label={t('entity.newVehicle.catalogue.validTo', 'Valid to')}
                helperText={t(
                  'entity.newVehicle.catalogue.validToHelp',
                  'Leave empty while the model year is still on sale.'
                )}
                isDisabled={!props.canWrite}
              />
            </Grid>
            <HStack spacing={2} align="center">
              <Icon value="action/info_outline" size={4} color="text.secondary" />
              <Text size="small" color="secondary">
                {t(
                  'entity.newVehicle.catalogue.pricedIn',
                  'Prices are in {currency}, net of VAT.',
                  {
                    currency: props.currency,
                  }
                )}
              </Text>
            </HStack>
            <Separator spacing={0} />
            <FormSection
              title={t(
                'entity.newVehicle.catalogue.importerPriceList.title',
                'Importer price list'
              )}
            >
              <ImporterPriceListFields
                control={control}
                names={IMPORTER_PRICE_LIST_FIELD_NAMES}
                isDisabled={!props.canWrite}
              />
            </FormSection>
            <Show when={props.canWrite}>
              <HStack justify="flex-end">
                <FormButton
                  control={control}
                  type="submit"
                  title={t('general.actions.saveChanges', 'Save changes')}
                />
              </HStack>
            </Show>
          </VStack>
        )}
      </Form>
    </Card>
  );
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function ModelYearShell() {
  const {modelYearId = '', tab = 'overview'} = useParams();
  const navigate = useNavigate();
  const formatDate = useDateTimeFormatter();
  const {tenantCurrency} = useTenant();
  const [canWrite, canPublish, canViewCost] = usePermissions({
    permissionKeys: [
      'newVehicleCatalogueWrite',
      'newVehicleCataloguePublish',
      'newVehicleCatalogueViewCost',
    ],
  });
  const yearQuery = useGetModelYearQuery({modelYearId});
  const year = yearQuery.data;
  const modelQuery = useGetModelQuery({modelId: year?.modelId ?? ''}, {skip: !year});
  const model = modelQuery.data;
  const makeQuery = useGetMakeQuery({makeId: model?.makeId ?? ''}, {skip: !model});
  const make = makeQuery.data;
  const {counts, publishableVariants, isMatrixFetching} = useModelYearCounts(modelYearId);
  const readiness = useGetModelYearReadinessQuery({modelYearId});
  const readinessChecks = readiness.data?.checks ?? [];
  // While a blocking check fails the server refuses to publish, so there is nothing to confirm yet.
  const failingWarnings = readiness.data?.ready ? failingChecks(readinessChecks, 'warning') : [];
  const [updateYear] = useUpdateModelYearMutation();
  const [archiveYear] = useArchiveModelYearMutation();
  const [failures, setFailures] = useState<ReadinessFailure[]>([]);
  const [isConfirmingWarnings, setIsConfirmingWarnings] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const goTab = (next: string) =>
    navigate(
      composePath(vehiclesRoutes.newVehicleCatalogueModelYear, {params: {modelYearId, tab: next}})
    );
  const preview = () =>
    navigate(
      composePath(MODEL_YEAR_PREVIEW_ROUTE, {
        params: {modelYearId, step: '1'},
      })
    );
  const {headerAction, confirmPublish} = useModelYearLifecycle({
    year,
    canPublish,
    publishableVariants,
    isReadinessFetching: readiness.isFetching,
    isMatrixFetching,
    hasWarnings: failingWarnings.length > 0,
    onWarnings: () => setIsConfirmingWarnings(true),
    onPublishError: (error) => {
      // A failed publish also invalidates readiness, so the details in this dialog refetch.
      const details = errorDetails(error);
      if (details.length) {
        setFailures(details);
      } else {
        handleCatalogueError(error);
      }
    },
  });
  const saveOverview = async (values: OverviewValues) => {
    if (!year) {
      return;
    }
    return await updateYear({
      modelYearId,
      modelYearWriteRequestBody: {
        modelId: year.modelId,
        name: values.code,
        catalogueName: values.catalogueName,
        vatRateType: values.vatRateType,
        validFrom: values.validFrom,
        validTo: values.validTo || null,
        status: year.status,
        pricelistVersionLabel: values.pricelistVersionLabel,
        importerPriceList: values.importerPriceList,
        version: values.version,
      },
    })
      .unwrap()
      .then((saved) => {
        showNotification.success(t('general.notifications.changesSaved', 'Changes saved'));
        return saved;
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error);
        return undefined;
      });
  };
  const openDuplicatedYear = (duplicatedId: string) => {
    navigate(
      composePath(vehiclesRoutes.newVehicleCatalogueModelYear, {
        params: {modelYearId: duplicatedId, tab: 'overview'},
      })
    );
    showNotification.success(t('entity.newVehicle.catalogue.duplicated', 'Model year duplicated'));
  };
  const archiveCurrentYear = () => {
    if (!year) {
      return;
    }
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveModelYear', 'Archive model year {name}?', {
        name: year.name,
      }),
      onConfirm: () =>
        void archiveYear({modelYearId, versionedWriteRequestBody: {version: year.version}})
          .unwrap()
          .catch(handleCatalogueError),
    });
  };
  const content: Record<TabId, React.ReactNode> = {
    // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
    overview: year ? (
      <Overview
        modelYear={year}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        counts={counts}
        onSave={saveOverview}
        onTab={goTab}
      />
    ) : null,
    'trim-levels': (
      <TrimLevels
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
      />
    ),
    engines: <Engines modelYearId={modelYearId} canWrite={Boolean(canWrite)} />,
    'variant-matrix': (
      <VariantMatrix
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
        modelYearVersion={year?.version ?? 1}
        pricelistVersionLabel={year?.pricelistVersionLabel ?? ''}
      />
    ),
    colours: (
      <Colours
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
      />
    ),
    upholstery: (
      <Upholsteries
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
      />
    ),
    'colour-upholstery': (
      <ColourUpholsteryCombinations
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
      />
    ),
    features: (
      <FeaturesTab
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
      />
    ),
    'equipment-by-trim': (
      <EquipmentByTrimTab
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
      />
    ),
    packages: (
      <PackagesTab
        modelYearId={modelYearId}
        currency={tenantCurrency}
        canWrite={Boolean(canWrite)}
        canViewCost={Boolean(canViewCost)}
      />
    ),
    rules: <RulesTab modelYearId={modelYearId} canWrite={Boolean(canWrite)} />,
  };
  const status = year?.status ?? 'draft';
  // dateShort is what the Valid from/to fields show, so the header matches the form below it.
  const validFrom = year ? formatDate('dateShort', parseDate(year.validFrom)) : '—';
  const validTo = year?.validTo
    ? formatDate('dateShort', parseDate(year.validTo))
    : t('entity.newVehicle.catalogue.openEnded', 'Open');

  return (
    <DataStatus
      isLoading={yearQuery.isLoading || modelQuery.isLoading || makeQuery.isLoading}
      isError={yearQuery.isError || modelQuery.isError || makeQuery.isError}
      errorMessage={t('entity.newVehicle.errors.modelYearNotFound', 'Model year not found')}
      errorAction={{
        title: t('entity.newVehicle.actions.backToCatalogue', 'Back to catalogue'),
        onClick: () => navigate(vehiclesRoutes.newVehicleCatalogue),
        variant: 'secondary',
      }}
      isEmpty={!year}
    >
      <Show when={year}>
        <VStack spacing={4}>
          <NewVehiclePageHeader
            breadcrumbs={[
              {label: t('page.vehicles.title', 'Vehicles'), href: vehiclesRoutes.home},
              {
                label: t('entity.newVehicle.configurations.title', 'New vehicle configurations'),
                href: vehiclesRoutes.newVehicleConfigurations,
              },
              {
                label: t('entity.newVehicle.catalogue.title', 'Catalogue'),
                href: vehiclesRoutes.newVehicleCatalogue,
              },
              {
                label: make?.name ?? '',
                href: composePath(vehiclesRoutes.newVehicleCatalogueMake, {
                  params: {makeId: make?.id ?? ''},
                }),
              },
              {
                label: model?.name ?? '',
                href: composePath(vehiclesRoutes.newVehicleCatalogueModel, {
                  params: {modelId: model?.id ?? ''},
                }),
              },
              {label: year?.name ?? ''},
            ]}
            title={`${make?.name ?? ''} ${model?.name ?? ''} · ${year?.name ?? ''}`}
            status={{
              label: getNewVehicleEnumLabel('modelYearStatus', status, t),
              colorScheme: getNewVehicleEnumColorScheme('modelYearStatus', status),
              isSubtle: true,
            }}
            parameters={[
              ...pricelistParameters(year),
              `${t('entity.newVehicle.catalogue.valid', 'Valid')} ${validFrom} → ${validTo}`,
              `${t('entity.newVehicle.catalogue.currency', 'Currency')} ${tenantCurrency}`,
            ]}
            actions={[
              ...(headerAction ? [headerAction] : []),
              ...(canWrite
                ? [
                    {
                      type: 'button' as const,
                      title: t('entity.newVehicle.catalogue.duplicate', 'Duplicate'),
                      variant: 'secondary' as const,
                      onClick: () => setDuplicateOpen(true),
                    },
                  ]
                : []),
              ...(canWrite || canPublish
                ? [
                    {
                      type: 'button' as const,
                      title: t(
                        'entity.newVehicle.catalogue.previewConfigurator',
                        'Preview as configurator'
                      ),
                      variant: 'secondary' as const,
                      onClick: preview,
                    },
                  ]
                : []),
              ...(canWrite
                ? [
                    {
                      type: 'dropdown-button' as const,
                      title: i18n.t('general.labels.more'),
                      variant: 'secondary' as const,
                      rightIcon: 'navigation/expand_more' as const,
                      menuItems: [
                        {
                          label: t(
                            'entity.newVehicle.catalogue.archiveModelYear',
                            'Archive model year'
                          ),
                          onClick: archiveCurrentYear,
                        },
                      ],
                    },
                  ]
                : []),
            ]}
          />
          <Tabs
            activeTabId={isTabId(tab) ? tab : 'overview'}
            onChange={goTab}
            hasSeparator
            isLazy
            shouldWrap
            tabs={tabs.map((id) => ({
              id,
              title: tabLabels[id],
              content: content[id],
              ...(typeof counts[id] === 'number'
                ? {badge: {value: counts[id].toString(), colorScheme: 'neutral' as const}}
                : {}),
            }))}
          />
        </VStack>
      </Show>
      <PublishBlockedDialog
        failures={failures}
        checks={readinessChecks}
        onClose={() => setFailures([])}
        onTab={goTab}
      />
      <PublishWarningsDialog
        isOpen={isConfirmingWarnings}
        warnings={failingWarnings}
        onClose={() => setIsConfirmingWarnings(false)}
        onPublish={confirmPublish}
        onTab={goTab}
      />
      <DuplicateModelYearDialog
        target={
          duplicateOpen && year
            ? {
                modelYearId,
                version: year.version,
                modelName: model?.name ?? '',
                validFrom: year.validFrom,
                validTo: year.validTo ?? null,
              }
            : undefined
        }
        onClose={() => setDuplicateOpen(false)}
        onDuplicated={openDuplicatedYear}
        onStaleVersion={() => void yearQuery.refetch()}
      />
    </DataStatus>
  );
}
